import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api, { formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/pages/admin/AdminDashboard";
import { PlusCircle, IndianRupee, TrendingUp, Handshake, Trophy } from "lucide-react";

const Stat = ({ label, value, sub, icon: Icon, testid, accent = "text-zinc-950" }) => (
  <Card className="p-6 border-zinc-200 shadow-none rounded-md bg-white">
    <div className="flex items-center justify-between mb-3">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <Icon size={18} strokeWidth={1.5} className="text-zinc-400" />
    </div>
    <div className={`text-3xl font-extrabold tracking-tighter font-heading tabular-nums ${accent}`} data-testid={testid}>{value}</div>
    {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
  </Card>
);

export default function SalesDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/stats/me").then((r) => setStats(r.data));
    api.get("/invoices").then((r) => setRecent(r.data.slice(0, 6)));
  }, []);

  const targetType = stats?.target_type || "turnover";
  const isTurnover = targetType === "turnover";
  const value = isTurnover ? stats?.attributed_invoice_value : stats?.attributed_margin;
  const pct = stats?.target_amount > 0 ? Math.min(stats?.progress_pct, 100) : 0;

  return (
    <Shell
      title="My Dashboard"
      subtitle={`Your attributed ${isTurnover ? "turnover" : "margin"} (with split applied automatically).`}
      actions={
        <Link to="/invoices/new">
          <Button className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="new-invoice-button">
            <PlusCircle size={16} className="mr-1" /> New Invoice
          </Button>
        </Link>
      }
    >
      <Card className="p-6 border-zinc-200 shadow-none rounded-md bg-white mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
              {isTurnover ? "Turnover Target" : "Margin Target"}
            </div>
            <h2 className="text-2xl font-extrabold tracking-tighter font-heading">
              {formatINR(value || 0)}{" "}
              <span className="text-base font-medium text-zinc-500">of {formatINR(stats?.target_amount || 0)}</span>
            </h2>
          </div>
          <div className="text-right">
            <div className="text-3xl font-extrabold tracking-tighter font-heading text-blue-700 tabular-nums" data-testid="my-progress-pct">
              {stats?.progress_pct ?? 0}%
            </div>
            <div className="text-xs text-zinc-500">to target</div>
          </div>
        </div>
        <Progress value={pct} className="h-2.5" />
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {isTurnover ? (
          <Stat label="Attributed Turnover" value={formatINR(stats?.attributed_invoice_value || 0)} sub="Approved invoices" icon={IndianRupee} testid="my-attr-value" />
        ) : (
          <Stat label="Attributed Margin" value={formatINR(stats?.attributed_margin || 0)} sub="Approved invoices" icon={TrendingUp} testid="my-attr-margin" accent="text-blue-700" />
        )}
        <Stat label="Closures · Referrals" value={`${stats?.closures ?? 0} · ${stats?.referrals ?? 0}`} sub="Lifetime" icon={Trophy} testid="my-counts" />
        <Stat label="Pending · Rejected" value={`${stats?.pending_count ?? 0} · ${stats?.rejected_count ?? 0}`} sub="Submitted by me" icon={Handshake} testid="my-pending-rejected" accent="text-amber-700" />
      </div>

      <Card className="border-zinc-200 shadow-none rounded-md overflow-hidden">
        <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="text-xl font-bold tracking-tight font-heading">Recent invoices</h2>
          <Link to="/invoices" className="text-sm font-semibold underline" data-testid="link-all-invoices">All →</Link>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="font-bold text-zinc-950">Invoice</TableHead>
              <TableHead className="font-bold text-zinc-950">Date</TableHead>
              <TableHead className="font-bold text-zinc-950">Customer · Project</TableHead>
              <TableHead className="font-bold text-zinc-950 text-right">Value</TableHead>
              {!isTurnover && <TableHead className="font-bold text-zinc-950 text-right">Margin</TableHead>}
              <TableHead className="font-bold text-zinc-950 text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recent.length === 0 && (
              <TableRow><TableCell colSpan={isTurnover ? 5 : 6} className="text-center py-10 text-zinc-500">No invoices yet.</TableCell></TableRow>
            )}
            {recent.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell className="font-semibold">{inv.invoice_no}</TableCell>
                <TableCell>{inv.invoice_date?.slice(0, 10)}</TableCell>
                <TableCell>
                  <div className="font-medium">{inv.customer_name}</div>
                  {inv.project && <div className="text-xs text-zinc-500">{inv.project}</div>}
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatINR(inv.invoice_value)}</TableCell>
                {!isTurnover && <TableCell className="text-right tabular-nums">{formatINR(inv.margin)}</TableCell>}
                <TableCell className="text-right"><StatusBadge status={inv.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Shell>
  );
}
